/**
 * "Show me" (approved mockup 2026-10-26): Getting started opens the right page first, then this
 * points at the exact spot with one short tip and Got it. No step counter, no chain of pop-ups.
 * The tip is kept for this browser tab only, so it survives the page change.
 */
import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useStore } from "../lib/store";

export interface CoachTip { key: string; target: string; title: string; body: string }
const KEY = "docvault.coach";
const EVENT = "docvault:coach";

/** Open a page, then point at something on it. */
export function showMe(tip: CoachTip, go: (to: string) => void, to: string) {
  try { sessionStorage.setItem(KEY, JSON.stringify(tip)); } catch { /* private mode: the page still opens */ }
  go(to);
  window.dispatchEvent(new Event(EVENT));
}

export function Coach() {
  const { reloadOnboarding } = useStore();
  const nav = useNavigate();
  const loc = useLocation();
  const [tip, setTip] = useState<CoachTip | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const read = () => { try { const v = sessionStorage.getItem(KEY); setTip(v ? (JSON.parse(v) as CoachTip) : null); } catch { setTip(null); } };
  useEffect(() => { read(); const on = () => read(); window.addEventListener(EVENT, on); return () => window.removeEventListener(EVENT, on); }, []);
  useEffect(() => { read(); }, [loc.pathname]);
  // The target may render a moment after the page opens: look for it for up to 4 seconds.
  useLayoutEffect(() => {
    if (!tip) { setRect(null); return; }
    let n = 0;
    const find = () => {
      const r = document.querySelector(tip.target)?.getBoundingClientRect();
      if (r && r.width > 0) { setRect(r); return true; }
      return false;
    };
    if (find()) return;
    const t = setInterval(() => { if (find() || ++n > 20) clearInterval(t); }, 200);
    const onResize = () => find();
    window.addEventListener("resize", onResize);
    return () => { clearInterval(t); window.removeEventListener("resize", onResize); };
  }, [tip, loc.pathname]);
  if (!tip || !rect) return null;
  const close = (back: boolean) => {
    try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
    setTip(null);
    void api.setOnboarding({ seen: tip.key }).then(() => reloadOnboarding());
    if (back) nav("/welcome");
  };
  const W = Math.min(340, window.innerWidth - 32);
  const right = rect.right + 20 + W < window.innerWidth;
  const pos: React.CSSProperties = right
    ? { left: rect.right + 20, top: Math.max(12, Math.min(rect.top + rect.height / 2 - 40, window.innerHeight - 220)) }
    : { left: Math.max(16, Math.min(rect.left, window.innerWidth - W - 16)), top: Math.min(rect.bottom + 14, window.innerHeight - 220) };
  return (
    <>
      <div className="tour-ring" style={{ left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12 }} />
      <div className="cm-pop" role="dialog" aria-label={tip.title} style={{ ...pos, width: W, position: "fixed" }}>
        {right && <div className="cm-arrow" style={{ top: Math.max(14, rect.top + rect.height / 2 - (pos.top as number) - 8) }} />}
        <b>{tip.title}</b>
        <p>{tip.body}</p>
        <div className="row-btns" style={{ justifyContent: "space-between", alignItems: "center", margin: 0 }}>
          <a className="doclink" onClick={() => close(true)}>Back to Getting started</a>
          <button className="btn btn-accent btn-sm" onClick={() => close(false)}>Got it</button>
        </div>
      </div>
    </>
  );
}
